import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight, MapPin, Smartphone, WifiOff, School } from "lucide-react";
import { Logo } from "@/components/landing/Navbar";

const FAQ = [
  ["Qui peut s'inscrire sur EDUKATI ?", "Les directeurs d'établissement et les enseignants. Chaque demande est examinée par l'administrateur EDUKATI avant l'activation du compte."],
  ["Combien de temps prend la validation ?", "Votre demande est examinée par l'équipe EDUKATI. Vous pourrez vous connecter dès qu'elle aura été approuvée ; l'équipe peut vous contacter si des informations manquent."],
  ["L'inscription est-elle payante ?", "L'envoi de la demande d'inscription est gratuit. Les conditions d'utilisation sont ensuite discutées directement avec chaque établissement."],
  ["EDUKATI fonctionne-t-il sur téléphone ?", "Oui. L'application est pensée d'abord pour le téléphone (saisie des notes, appel, suivi) et reste légère pour les connexions lentes."],
  ["Je suis enseignant en multigrade ou en double flux, puis-je l'utiliser ?", "Oui. Lors de l'inscription, vous sélectionnez plusieurs niveaux et précisez votre organisation : classe simple, multigrade ou double flux."],
  ["Mon école est déjà inscrite, que dois-je faire ?", "Recherchez-la dans le formulaire enseignant et sélectionnez-la. Votre rattachement sera confirmé par le directeur ou l'administrateur."],
  ["Les parents ont-ils un accès ?", "Oui, un espace parent permet de consulter la fiche et les résultats d'un élève grâce à son matricule ou au QR Code de sa carte scolaire."],
];

export function About() {
  const pts = [[School, "Pour les écoles élémentaires", "Du CI au CM2, publiques, privées, franco-arabes ou communautaires."], [MapPin, "Adapté au Sénégal", "IA, IEF, bulletins et matricules au format des écoles sénégalaises."], [Smartphone, "Mobile d'abord", "Une interface simple, utilisable sur un téléphone."], [WifiOff, "Connexion légère", "Des pages optimisées pour les réseaux lents."]];
  return (
    <section id="apropos" className="py-16 md:py-24 scroll-mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="text-xs font-bold text-[#1F7A63] uppercase tracking-[0.18em]">À propos</div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>Une plateforme née sur le terrain</h2>
          <p className="mt-4 text-[#5C736A] leading-relaxed">EDUKATI a été conçu avec et pour les directeurs et enseignants du Sénégal, afin de remplacer les registres papier et les tableurs dispersés par un outil unique, fiable et facile à prendre en main.</p>
        </div>
        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
          {pts.map(([Icon, t, d]) => (
            <div key={t} className="bg-white rounded-2xl border border-[#E2E8F0] p-5">
              <Icon className="w-5 h-5 text-[#1F7A63]" />
              <div className="mt-3 font-bold" style={{ fontFamily: "Outfit" }}>{t}</div>
              <p className="mt-1 text-sm text-[#5C736A]">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Faq() {
  return (
    <section id="faq" className="py-16 md:py-24 bg-white border-y border-[#E2E8F0]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="text-xs font-bold text-[#1F7A63] uppercase tracking-[0.18em]">FAQ</div>
        <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>Questions fréquentes</h2>
        <Accordion type="single" collapsible className="mt-8" data-testid="faq-list">
          {FAQ.map(([q, a], i) => (
            <AccordionItem key={q} value={`q${i}`}>
              <AccordionTrigger className="text-left text-base font-semibold hover:no-underline" data-testid={`faq-q-${i}`}>{q}</AccordionTrigger>
              <AccordionContent className="text-[#5C736A] leading-relaxed">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

export function Contact() {
  return (
    <section id="contact" className="py-16 md:py-24 scroll-mt-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-[#1F7A63] text-white px-6 py-12 sm:px-12 sm:py-16">
          <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-[#E6B800]/25" />
          <div className="absolute -left-10 -bottom-20 w-56 h-56 rounded-full bg-white/5" />
          <div className="relative max-w-2xl">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white" style={{ fontFamily: "Outfit" }}>Vous souhaitez équiper votre école avec EDUKATI ?</h2>
            <p className="mt-4 text-white/85 leading-relaxed">Inscrivez-vous pour demander l&apos;activation de votre compte. Notre équipe examinera votre demande et vous contactera si nécessaire.</p>
            <Link to="/inscription" className="inline-block mt-8" data-testid="contact-signup-btn">
              <Button className="h-12 px-7 text-base bg-[#E6B800] hover:bg-[#b38f00] text-[#1A2E28] font-bold">Créer mon compte <ArrowRight className="w-4 h-4 ml-2" /></Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="bg-[#1A2E28] text-white/70">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid gap-8 md:grid-cols-4">
        <div className="md:col-span-2">
          <Logo light />
          <p className="mt-4 text-sm max-w-sm">La gestion scolaire intelligente, simple et accessible, pour les écoles du Sénégal.</p>
        </div>
        <div className="text-sm space-y-2">
          <div className="font-semibold text-white mb-3">Plateforme</div>
          <a href="#fonctionnalites" className="block hover:text-white">Fonctionnalités</a>
          <a href="#directeur" className="block hover:text-white">Espace Directeur</a>
          <a href="#enseignant" className="block hover:text-white">Espace Enseignant</a>
          <a href="#faq" className="block hover:text-white">FAQ</a>
        </div>
        <div className="text-sm space-y-2">
          <div className="font-semibold text-white mb-3">Accès</div>
          <Link to="/login" className="block hover:text-white">Connexion</Link>
          <Link to="/inscription" className="block hover:text-white">Créer un compte</Link>
          <Link to="/parent" className="block hover:text-white" data-testid="footer-parent-link">Espace parent</Link>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 text-xs">© {new Date().getFullYear()} EDUKATI — Sénégal. Tous droits réservés.</div>
      </div>
    </footer>
  );
}
